import { PapyrusDocumentRepresentation } from "./DocumentModels";
// FHIR
import { Bundle } from "fhir/r4";

export type ResolvedDocumentAttachment = {
    contentType: string;
    sourceUrl?: string;
    objectUrl?: string;
    text?: string;
    json?: unknown;
    bundle?: Bundle;
};

export default class DocumentAttachmentService {
    static async resolve(
        representation: PapyrusDocumentRepresentation,
        fhirClient: any
    ): Promise<ResolvedDocumentAttachment> {
        if (representation.attachmentData) {
            return DocumentAttachmentService.resolveData(
                representation.contentType,
                representation.attachmentData
            );
        }

        if (representation.attachmentUrl?.startsWith("Binary/")) {
            const binaryId = representation.attachmentUrl.replace("Binary/", "");
            const binary = await fhirClient.read({
                resourceType: "Binary",
                id: binaryId,
            });

            return DocumentAttachmentService.resolveData(
                binary.contentType ?? representation.contentType,
                binary.data
            );
        }

        if (representation.attachmentUrl?.startsWith("Bundle/")) {
            const bundleId = representation.attachmentUrl.replace("Bundle/", "");
            const bundle = await fhirClient.read({
                resourceType: "Bundle",
                id: bundleId,
            });

            return {contentType: representation.contentType, bundle};
        }

        if (representation.attachmentUrl) {
            return {
                contentType: representation.contentType,
                sourceUrl: representation.attachmentUrl,
            };
        }

        return {
            contentType: representation.contentType,
        };
    }

    private static resolveData(contentType: string, base64Data?: string): ResolvedDocumentAttachment {
        if (!base64Data) {
            return { contentType };
        }

        const binaryString = window.atob(base64Data);
        const bytes = Uint8Array.from(binaryString, (character) => character.charCodeAt(0));
        const blob = new Blob([bytes], { type: contentType });
        const objectUrl = URL.createObjectURL(blob);

        if (contentType === "application/json" || contentType === "application/fhir+json") {
            const text = new TextDecoder().decode(bytes);
            return {
                contentType,
                objectUrl,
                text,
                json: DocumentAttachmentService.parseJson(text),
            };
        }

        if (contentType.startsWith("text/")) {
            return {
                contentType,
                objectUrl,
                text: new TextDecoder().decode(bytes),
            };
        }

        return {
            contentType,
            objectUrl,
        };
    }

    private static parseJson(text: string): unknown | undefined {
        try {
            return JSON.parse(text);
        } catch (error) {
            return undefined;
        }
    }
}
